import type { Deck, Requirement } from '@/api/generated/game';
import {
  DeckAction,
  DeckClown,
  DeckDrink,
  DeckTruth,
  ReqCamera,
  ReqCoin,
  ReqDice,
  ReqMinigame,
  ReqRandomizer,
  ReqTimer,
} from '@/api/generated/game';
import type { DeckBundle } from '@/api/rest';

export interface DeckCount {
  deck: Deck;
  label: string;
  emoji: string;
  /** Класс заливки полоски: токен колоды из tailwind.config.js. */
  swatch: string;
  count: number;
}

export interface DeckSummary {
  total: number;
  decks: DeckCount[];
  /** Уровни 1–3 в порядке возрастания, подписи как на экране 02. */
  intensity: Array<{ label: string; count: number }>;
  /** Реквизит, который в паке вообще встречается, в каноническом порядке. */
  requirements: string[];
}

// Порядок, подписи и знаки — как в макете (design-spec.md, «Карточки колод»).
const DECKS: ReadonlyArray<Omit<DeckCount, 'count'>> = [
  { deck: DeckTruth, label: 'Правда', emoji: '👀', swatch: 'bg-truth' },
  { deck: DeckAction, label: 'Действие', emoji: '💃', swatch: 'bg-action' },
  { deck: DeckDrink, label: 'Алкоголь', emoji: '🍻', swatch: 'bg-drink' },
  { deck: DeckClown, label: 'Клоун', emoji: '🤡', swatch: 'bg-clown' },
];

const INTENSITY_LABELS = ['мягко', 'остро', 'жёстко'];

const REQUIREMENT_LABELS: ReadonlyArray<[Requirement, string]> = [
  [ReqTimer, 'таймер'],
  [ReqCoin, 'монета'],
  [ReqDice, 'кубик'],
  [ReqCamera, 'камера'],
  [ReqRandomizer, 'барабан'],
  [ReqMinigame, 'мини-игра'],
];

/**
 * Пересчёт бандла в то, что показывают в витрине: сколько карточек в каждой
 * колоде, как они разложены по уровню и какой реквизит понадобится.
 *
 * Текстов карточек здесь нет и быть не должно: прочитанные заранее задания —
 * это испорченная партия, а бандл приезжает целиком только потому, что ручка
 * рассчитана на офлайн-клиента (../clown-back/api/openapi.yaml).
 *
 * Считать по чужим уровням незачем: `intensity` ограничена контрактом 1..3.
 */
export function summarizeBundle(bundle: DeckBundle): DeckSummary {
  const perDeck = new Map<Deck, number>();
  const perIntensity = [0, 0, 0];
  const seenRequirements = new Set<Requirement>();

  for (const card of bundle.cards) {
    perDeck.set(card.deck, (perDeck.get(card.deck) ?? 0) + 1);
    const level = card.intensity;
    if (level >= 1 && level <= 3) perIntensity[level - 1] = (perIntensity[level - 1] ?? 0) + 1;
    for (const requirement of card.requires ?? []) seenRequirements.add(requirement);
  }

  return {
    total: bundle.cards.length,
    decks: DECKS.map((deck) => ({ ...deck, count: perDeck.get(deck.deck) ?? 0 })),
    intensity: INTENSITY_LABELS.map((label, index) => ({
      label,
      count: perIntensity[index] ?? 0,
    })),
    requirements: REQUIREMENT_LABELS.filter(([key]) => seenRequirements.has(key)).map(
      ([, label]) => label,
    ),
  };
}
